import { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router'
import { useIntl } from 'react-intl'
import { KTIcon, toAbsoluteUrl } from '../../../helpers'
import { HeaderUserMenu } from '../../../partials'
import { AppDispatch } from '../../../../app/redux/store'
import { getOrderList } from '../../../../app/redux/orders/orderSlice'
import { CoreOrderResponse } from '../../../../app/redux/orders/_orderTypes'
import { getDocumentList } from '../../../../app/redux/documents/documentSlice'
import { CoreDocumentResponse } from '../../../../app/redux/documents/_documentTypes'
import { createOrderBy } from '../../../../app/axios/odata'
import { handleError } from '../../../../app/utils/handleError'
import { buildOrdersFilterQuery, OrderFilterValues } from '../../../../app/modules/orders/helpers/OrdersHelper'
import { buildDocumentsFilterQuery, DocumentFilterValues } from '../../../../app/modules/documents/helpers/DocumentsHelper'

const itemClass = 'ms-1 ms-md-4'
const btnClass =
  'btn btn-icon btn-custom btn-icon-muted btn-active-light btn-active-color-primary w-35px h-35px'
const userAvatarClass = 'symbol-35px'
const btnIconClass = 'fs-1'
const GLOBAL_SEARCH_RESULT_LIMIT = 8
const GLOBAL_SEARCH_SORT_FIELDS = ['number', 'created', 'lastUpdated'] as const

const Navbar = () => {
  const intl = useIntl()
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const searchContainerRef = useRef<HTMLDivElement | null>(null)

  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  const [hasSearched, setHasSearched] = useState(false)
  const [orderResults, setOrderResults] = useState<CoreOrderResponse[]>([])
  const [documentResults, setDocumentResults] = useState<CoreDocumentResponse[]>([])
  const [orderResultCount, setOrderResultCount] = useState(0)
  const [documentResultCount, setDocumentResultCount] = useState(0)

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!searchContainerRef.current?.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [])

  const clearSearchState = () => {
    setSearchError(null)
    setHasSearched(false)
    setOrderResults([])
    setDocumentResults([])
    setOrderResultCount(0)
    setDocumentResultCount(0)
  }

  const runGlobalSearch = async () => {
    const normalizedTerm = searchTerm.trim()
    if (!normalizedTerm) {
      clearSearchState()
      return
    }

    const orderSearchValues: OrderFilterValues = {
      search: normalizedTerm,
      experience: [],
      sla: [],
      status: [],
      dateRange: { startDate: null, endDate: null },
      page: 1,
      perPage: GLOBAL_SEARCH_RESULT_LIMIT,
      totalRows: 0,
      loading: false,
      sortState: { field: 'number', direction: 'desc' },
    }

    const documentSearchValues: DocumentFilterValues = {
      search: normalizedTerm,
      channel: [],
      type: [],
      status: [],
      experience: '',
      dateRange: { startDate: null, endDate: null },
      page: 1,
      perPage: GLOBAL_SEARCH_RESULT_LIMIT,
      totalRows: 0,
      loading: false,
      sortState: { field: 'number', direction: 'desc' },
    }

    try {
      setSearching(true)
      setSearchError(null)

      const [ordersResponse, documentsResponse] = await Promise.all([
        dispatch(getOrderList({
          filter: buildOrdersFilterQuery(orderSearchValues, true),
          orderby: createOrderBy('number', 'desc', GLOBAL_SEARCH_SORT_FIELDS),
          top: GLOBAL_SEARCH_RESULT_LIMIT,
          skip: 0,
        })).unwrap(),
        dispatch(getDocumentList({
          filter: buildDocumentsFilterQuery(documentSearchValues, true),
          orderby: createOrderBy('number', 'desc', GLOBAL_SEARCH_SORT_FIELDS),
          top: GLOBAL_SEARCH_RESULT_LIMIT,
          skip: 0,
        })).unwrap(),
      ])

      const totalMatches = ordersResponse.totalCount + documentsResponse.totalCount

      if (totalMatches === 1) {
        if (ordersResponse.totalCount === 1 && ordersResponse.items.length > 0) {
          setIsOpen(false)
          navigate(`/orderDetails/${encodeURIComponent(ordersResponse.items[0].number)}`)
          return
        }

        if (documentsResponse.totalCount === 1 && documentsResponse.items.length > 0) {
          setIsOpen(false)
          navigate(`/documentDetails/${encodeURIComponent(documentsResponse.items[0].number)}`)
          return
        }
      }

      setOrderResults(ordersResponse.items)
      setDocumentResults(documentsResponse.items)
      setOrderResultCount(ordersResponse.totalCount)
      setDocumentResultCount(documentsResponse.totalCount)
      setHasSearched(true)
      setIsOpen(true)
    } catch (requestError) {
      setSearchError(handleError(requestError))
      setOrderResults([])
      setDocumentResults([])
      setOrderResultCount(0)
      setDocumentResultCount(0)
      setHasSearched(true)
      setIsOpen(true)
    } finally {
      setSearching(false)
    }
  }

  return (
    <div className='app-navbar flex-shrink-0'>
      <div className={clsx('app-navbar-item app-navbar-search-item', itemClass)} ref={searchContainerRef}>
        <div className='app-navbar-global-search'>
          <div className='app-navbar-global-search-input-wrap'>
            <KTIcon iconName='magnifier' className='app-navbar-global-search-input-icon fs-3' />
            <input
              type='search'
              className='form-control app-navbar-global-search-input'
              placeholder={intl.formatMessage({ id: 'DASHBOARD.GLOBAL_SEARCH_PLACEHOLDER' })}
              value={searchTerm}
              onFocus={() => setIsOpen(true)}
              onChange={(event) => {
                const nextValue = event.target.value
                setSearchTerm(nextValue)
                if (!nextValue.trim()) {
                  clearSearchState()
                }
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  runGlobalSearch()
                }
              }}
              aria-label={intl.formatMessage({ id: 'DASHBOARD.GLOBAL_SEARCH' })}
            />
          </div>

          <button
            type='button'
            className='btn btn-primary app-navbar-global-search-button'
            onClick={runGlobalSearch}
            disabled={searching || !searchTerm.trim()}
          >
            {searching ? <span className='spinner-border spinner-border-sm' aria-hidden='true' /> : <KTIcon iconName='magnifier' className='fs-3' />}
          </button>

          {isOpen && (searching || searchError || hasSearched) && (
            <div className='app-navbar-global-search-dropdown'>
              {searchError && <div className='alert alert-danger m-0'><div className='alert-text fw-semibold'>{searchError}</div></div>}

              {!searchError && searching && (
                <div className='app-navbar-global-search-empty'>
                  <span className='spinner-border spinner-border-sm' aria-hidden='true' />
                  <span>{intl.formatMessage({ id: 'GENERAL.LOADING' })}</span>
                </div>
              )}

              {!searchError && !searching && hasSearched && (orderResultCount + documentResultCount) === 0 && (
                <div className='app-navbar-global-search-empty'>
                  <KTIcon iconName='magnifier' className='fs-3' />
                  <span>{intl.formatMessage({ id: 'DASHBOARD.GLOBAL_SEARCH_NO_RESULTS' })}</span>
                </div>
              )}

              {!searchError && !searching && (orderResultCount + documentResultCount) > 0 && (
                <div className='app-navbar-global-search-results'>
                  {orderResultCount > 0 && (
                    <section className='app-navbar-global-search-group'>
                      <header>
                        <span className='app-navbar-global-search-pill app-navbar-global-search-pill--orders'>{intl.formatMessage({ id: 'MENU.ORDERS' })}</span>
                        <strong>{intl.formatMessage({ id: 'DASHBOARD.GLOBAL_SEARCH_ORDER_RESULTS' }, { count: orderResultCount })}</strong>
                      </header>
                      <ul>
                        {orderResults.map((order) => (
                          <li key={order.number}>
                            <button
                              type='button'
                              className='app-navbar-global-search-result-link'
                              onClick={() => {
                                setIsOpen(false)
                                navigate(`/orderDetails/${encodeURIComponent(order.number)}`)
                              }}
                            >
                              <span className='app-navbar-global-search-result-main'>
                                <KTIcon iconName='rocket' className='fs-3' />
                                <b>{order.number}</b>
                              </span>
                              <span className='app-navbar-global-search-result-meta'>
                                <small>{order.status}</small>
                                {order.clientReference && <small>{intl.formatMessage({ id: 'GENERAL.REFERENCE' })}: {order.clientReference}</small>}
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}

                  {documentResultCount > 0 && (
                    <section className='app-navbar-global-search-group'>
                      <header>
                        <span className='app-navbar-global-search-pill app-navbar-global-search-pill--documents'>{intl.formatMessage({ id: 'MENU.DOCUMENTS' })}</span>
                        <strong>{intl.formatMessage({ id: 'DASHBOARD.GLOBAL_SEARCH_DOCUMENT_RESULTS' }, { count: documentResultCount })}</strong>
                      </header>
                      <ul>
                        {documentResults.map((document) => (
                          <li key={document.number}>
                            <button
                              type='button'
                              className='app-navbar-global-search-result-link'
                              onClick={() => {
                                setIsOpen(false)
                                navigate(`/documentDetails/${encodeURIComponent(document.number)}`)
                              }}
                            >
                              <span className='app-navbar-global-search-result-main'>
                                <KTIcon iconName='file' className='fs-3' />
                                <b>{document.number}</b>
                              </span>
                              <span className='app-navbar-global-search-result-meta'>
                                <small>{document.status}</small>
                                {document.linkedOrderNumber && <small>{intl.formatMessage({ id: 'DOCUMENT.LINKED_ORDER' })}: {document.linkedOrderNumber}</small>}
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}

                  {(orderResultCount > orderResults.length || documentResultCount > documentResults.length) && (
                    <p className='app-navbar-global-search-limit'>
                      {intl.formatMessage(
                        { id: 'DASHBOARD.GLOBAL_SEARCH_RESULTS_LIMIT' },
                        { shown: orderResults.length + documentResults.length, total: orderResultCount + documentResultCount }
                      )}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className={clsx('app-navbar-item', itemClass)}>
        <div id='kt_activities_toggle' className={btnClass}>
          <KTIcon iconName='message-text' className={btnIconClass} />
        </div>
      </div>

      <div className={clsx('app-navbar-item', itemClass)}>
        <div
          className={clsx('cursor-pointer symbol', userAvatarClass)}
          data-kt-menu-trigger="{default: 'click'}"
          data-kt-menu-attach='parent'
          data-kt-menu-placement='bottom-end'
        >
          <img src={toAbsoluteUrl('media/avatars/blank.png')} alt='' />
        </div>
        <HeaderUserMenu />
      </div>
    </div>
  )
}

export { Navbar }
